import R from 'ramda'
import React from 'react';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import subscribeMiddleware from 'redux-subscribe';
import promiseMiddleware from 'redux-promise-middleware';
import thunkMiddleware from 'redux-thunk';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import ReduxSweetAlert, { reducer as swalReducer } from 'react-redux-sweetalert';
import { container } from './container';

const app = {
    store: null,
    containers: {},
    lazyContainers: {},
    loadingContainers: {},
    render: (container, id, props) => {
        // Check if container is already loaded
        if (app.containers[container]) {
            const Container = app.containers[container];
            return renderElement(Container, id, undefined, props);
        }

        // Check if container is lazy-loaded
        if (app.lazyContainers[container]) {
            // Prevent multiple simultaneous loads
            if (app.loadingContainers[container]) {
                return app.loadingContainers[container].then(() => {
                    const Container = app.containers[container];
                    return renderElement(Container, id, undefined, props);
                });
            }

            // Load the container
            const loadPromise = app.lazyContainers[container]()
                .then(module => {
                    const Container = module.default || module;
                    if (!Container) {
                        throw new Error(`Container "${container}" has no default export`);
                    }
                    app.containers[container] = Container;
                    delete app.loadingContainers[container];
                    return renderElement(Container, id, undefined, props);
                })
                .catch(error => {
                    console.error(`Failed to load container "${container}":`, error);
                    delete app.loadingContainers[container];
                    // Return null instead of undefined to prevent further errors
                    return null;
                });

            app.loadingContainers[container] = loadPromise;
            return loadPromise;
        }

        console.error(`Container "${container}" not found! Available containers:`, Object.keys(app.containers));
        showStaleBundleMessage(id, container);
        return null;
    },
};

/**
 * Tell the user their browser is running an outdated bundle, in the element the page
 * expected React to mount into.
 *
 * A missing container has one realistic cause in production: the template shipped in a
 * deploy that the cached bundle predates, so it asks for a container that build never
 * registered. Django templates are never cached while the bundles are cached for 30 days,
 * so the two can disagree for a month.
 *
 * Before this, both failure paths logged to the console and returned — which left a
 * COMPLETELY blank page. `/student/drills/` sat empty that way for a student who had
 * drills waiting (2026-08-11), and nothing on screen suggested a reload would fix it.
 * A console error nobody opens is not a user-visible failure.
 */
function showStaleBundleMessage(id, container) {
    const element = document.getElementById(id);
    if (!element) {
        return;
    }
    // Plain DOM, not React: React is exactly what is failing here, and this message must
    // not depend on the machinery it is reporting the failure of.
    element.innerHTML =
        '<div style="margin:20px;padding:15px;border:1px solid #f0ad4e;' +
        'background:#fcf8e3;color:#8a6d3b;border-radius:3px">' +
        '<strong>Обновите страницу</strong><br>' +
        'Ваш браузер использует устаревшую версию сайта. ' +
        'Нажмите Ctrl+F5 (Cmd+Shift+R на Mac), чтобы загрузить актуальную.' +
        '</div>';
    console.error(`Stale bundle: "${container}" is missing from this build.`);
}

export function initStore(
    reducers,
    initialState = {},
    persistState = f => f
) {
    const reducer = combineReducers({
        ...reducers,
        sweetalert: swalReducer,
    });

    app.store = createStore(reducer, initialState, compose(
        applyMiddleware(
            thunkMiddleware.withExtraArgument(container),
            promiseMiddleware(),
            subscribeMiddleware,
        ),
        persistState,
        window.devToolsExtension ? window.devToolsExtension() : f => f,
    ));
    renderElement(ReduxSweetAlert, 'swal');

    window.APP = app;
    if (window.RENDER_QUEUE !== undefined) {
        R.forEach(item => app.render(item.container, item.id, item.props), window.RENDER_QUEUE);
    }
}

export function addContainer(name, container) {
    app.containers[name] = container;
}

export function addLazyContainer(name, loader) {
    app.lazyContainers[name] = loader;
}

export function getStore() {
    return app.store;
}

export function renderElement(Container, id, init = () => {}, props = {}) {
    const element = document.getElementById(id);

    if (element) {
        if (!Container) {
            console.error(`Container for id "${id}" is undefined! Available containers:`, Object.keys(app.containers));
            // Same reasoning as the lookup miss above: returning quietly leaves a blank
            // page, which reads as "there is nothing here" rather than "reload me".
            showStaleBundleMessage(id, id);
            return;
        }
        render(
            <Provider store={getStore()}>
                <Container {...props} />
            </Provider>,
            element,
        );

        init(getStore());
    }
}



// WEBPACK FOOTER //
// ./src/utils/store.js